/**
 * TemplateBrowser Component
 * 
 * Full-screen modal for browsing trip templates with search, filter, and sort capabilities.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Modal,
  FlatList,
  Pressable,
  TextInput,
  ScrollView,
  RefreshControl,
  useWindowDimensions,
  type TextInput as TextInputType,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, SubHeading, Body, Caption } from '../ui/Text';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ErrorState } from '../ui/ErrorState';
import { TemplateCard, TemplateCardSkeleton } from './TemplateCard';
import { fetchTripTemplates, filterTemplates, sortTemplates } from '@/services/tripTemplates';
import { useUserStore } from '@/state/userStore';
import type {
  TripTemplate,
  TemplateFilter,
  TemplateSortBy,
  TemplateDifficulty,
  TravelStyle,
} from '@/types/tripTemplates';
import { TEMPLATE_TOPIC_FILTERS } from '@/utils/templateTags';
import { logTemplateDeepLinkAnalytics } from '@/services/appleDeepLinkSurface';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

type TemplateBrowserProps = {
  visible: boolean;
  onClose: () => void;
  onDismiss?: () => void;
  onSelectTemplate: (template: TripTemplate, source?: 'browser' | 'widget', topicTag?: string) => void;
  onCreateTemplate?: () => void; // New prop for opening template builder
  /** Pre-select a topic/collection tag when opened from a campaign deeplink (one-shot). */
  initialTopicTag?: string;
  /** Campaign ref/utm preserved from `/templates?...` links (one-shot). */
  deeplinkRef?: string;
  deeplinkUtmSource?: string;
  /** Called after the deeplink topic seed is applied so the parent can clear it. */
  onInitialTopicConsumed?: () => void;
  /** Called after deeplink attribution analytics are logged so the parent can clear ref/utm. */
  onDeeplinkAttributionConsumed?: () => void;
};

const SORT_LABELS: Record<TemplateSortBy, string> = {
  popular: 'Popular',
  newest: 'Newest',
  'budget-low': 'Budget',
  'budget-high': 'Budget (high)',
  'duration-short': 'Duration',
  'duration-long': 'Duration (long)',
  rating: 'Rating',
};

/** Mutually exclusive quick-view chips in the template browser header. */
type TemplateQuickView = 'all' | 'popular' | 'budget-low' | 'duration-short';

const QUICK_VIEW_OPTIONS: TemplateQuickView[] = ['all', 'popular', 'budget-low', 'duration-short'];

const QUICK_VIEW_LABELS: Record<TemplateQuickView, string> = {
  all: 'All',
  popular: SORT_LABELS.popular,
  'budget-low': SORT_LABELS['budget-low'],
  'duration-short': SORT_LABELS['duration-short'],
};

function quickViewToSortBy(view: TemplateQuickView): TemplateSortBy {
  return view === 'all' ? 'popular' : view;
}

export function TemplateBrowser({
  visible,
  onClose,
  onDismiss,
  onSelectTemplate,
  onCreateTemplate,
  initialTopicTag,
  deeplinkRef,
  deeplinkUtmSource,
  onInitialTopicConsumed,
  onDeeplinkAttributionConsumed,
}: TemplateBrowserProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);

  const [templates, setTemplates] = useState<TripTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [localSearchInput, setLocalSearchInput] = useState(''); // Local state for immediate input updates
  const searchInputRef = useRef<TextInputType>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [quickView, setQuickView] = useState<TemplateQuickView>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Filter state
  const [selectedDifficulty, setSelectedDifficulty] = useState<TemplateDifficulty[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<TravelStyle[]>([]);
  const [durationRange, setDurationRange] = useState<{ min?: number; max?: number }>({});
  const [budgetRange, setBudgetRange] = useState<{ min?: number; max?: number }>({});
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [selectedTopicTags, setSelectedTopicTags] = useState<string[]>([]);
  /** Deeplink topic already seeded this session — prevents re-applying after the user clears filters. */
  const appliedInitialTopicRef = useRef<string | undefined>(undefined);
  const loggedOpenRef = useRef(false);
  const loggedFilterSignatureRef = useRef('');

  // Calculate number of columns based on screen width
  const numColumns = width < 600 ? 1 : width < 900 ? 2 : 3;

  // Load templates
  const loadTemplates = useCallback(async (useCache: boolean = true) => {
    try {
      setTemplateLoadError(null);
      setLoading(true);
      const fetchedTemplates = await fetchTripTemplates(useCache, { skipImageBackfill: true });
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error('[TemplateBrowser] Error loading templates:', error);
      setTemplateLoadError("Couldn't load templates. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      loggedOpenRef.current = false;
      return;
    }
    if (!loggedOpenRef.current) {
      loggedOpenRef.current = true;
      const isDeeplinkOpen = Boolean(initialTopicTag || deeplinkRef || deeplinkUtmSource);
      if (isDeeplinkOpen) {
        const authState: 'signed_out' | 'signed_in_not_onboarded' | 'signed_in_onboarded' = !isAuthenticated
          ? 'signed_out'
          : hasCompletedOnboarding
            ? 'signed_in_onboarded'
            : 'signed_in_not_onboarded';
        logTemplateDeepLinkAnalytics({
          topic: initialTopicTag,
          ref: deeplinkRef,
          utmSource: deeplinkUtmSource,
          authState,
        });
        onDeeplinkAttributionConsumed?.();
      }
      void logAnalyticsEvent('template_browser_opened', {
        source: isDeeplinkOpen ? 'deeplink' : 'unknown',
        topicTag: initialTopicTag,
      });
    }

    let isActive = true;
    const refreshTemplates = async () => {
      // Render cached Firestore template data immediately; each card resolves its image independently.
      await loadTemplates(true);
      if (isActive) {
        await loadTemplates(false);
      }
    };

    void refreshTemplates();

    return () => {
      isActive = false;
    };
  }, [visible, loadTemplates, initialTopicTag, deeplinkRef, deeplinkUtmSource, isAuthenticated, hasCompletedOnboarding, onDeeplinkAttributionConsumed]);

  const resetBrowserState = useCallback(() => {
    setSelectedDifficulty([]);
    setSelectedStyles([]);
    setDurationRange({});
    setBudgetRange({});
    setFeaturedOnly(false);
    setSelectedTopicTags([]);
    setSearchQuery('');
    setLocalSearchInput('');
    setQuickView('all');
    setShowFilters(false);
    appliedInitialTopicRef.current = undefined;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      resetBrowserState();
      return;
    }
    if (initialTopicTag && appliedInitialTopicRef.current !== initialTopicTag) {
      setSelectedTopicTags([initialTopicTag]);
      setShowFilters(true);
      appliedInitialTopicRef.current = initialTopicTag;
      onInitialTopicConsumed?.();
    }
  }, [visible, initialTopicTag, onInitialTopicConsumed, resetBrowserState]);

  const clearTopicFilters = useCallback(() => {
    setSelectedTopicTags([]);
    onInitialTopicConsumed?.();
  }, [onInitialTopicConsumed]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadTemplates(false); // Force refresh from Firestore
    setRefreshing(false);
  }, [loadTemplates]);

  // Approved/live templates visible in the marketplace (excludes pending drafts).
  const marketplaceTemplates = useMemo(() => filterTemplates(templates, {}), [templates]);

  // Apply filters and sorting
  const filteredAndSortedTemplates = useMemo(() => {
    const filter: TemplateFilter = {
      searchQuery: searchQuery.trim(),
      difficulty: selectedDifficulty.length > 0 ? selectedDifficulty : undefined,
      travelStyle: selectedStyles.length > 0 ? selectedStyles : undefined,
      minDuration: durationRange.min,
      maxDuration: durationRange.max,
      minBudget: budgetRange.min,
      maxBudget: budgetRange.max,
      featuredOnly,
      tags: selectedTopicTags.length > 0 ? selectedTopicTags : undefined,
    };

    const filtered = filterTemplates(templates, filter);
    return sortTemplates(filtered, quickViewToSortBy(quickView));
  }, [
    templates,
    searchQuery,
    selectedDifficulty,
    selectedStyles,
    durationRange,
    budgetRange,
    featuredOnly,
    quickView,
    selectedTopicTags,
  ]);

  useEffect(() => {
    if (!visible) return;
    const query = searchQuery.trim();
    if (!query) return;
    void logAnalyticsEvent('template_search', {
      query,
      resultsCount: filteredAndSortedTemplates.length,
      topicTag: selectedTopicTags[0],
    });
  }, [visible, searchQuery, filteredAndSortedTemplates.length, selectedTopicTags]);

  useEffect(() => {
    if (!visible) return;
    const hasAnyFilter =
      selectedDifficulty.length > 0 ||
      selectedStyles.length > 0 ||
      durationRange.min !== undefined ||
      durationRange.max !== undefined ||
      budgetRange.min !== undefined ||
      budgetRange.max !== undefined ||
      featuredOnly ||
      selectedTopicTags.length > 0;
    if (!hasAnyFilter) {
      loggedFilterSignatureRef.current = '';
      return;
    }
    const signature = JSON.stringify({
      selectedDifficulty,
      selectedStyles,
      durationRange,
      budgetRange,
      featuredOnly,
      selectedTopicTags,
    });
    if (signature === loggedFilterSignatureRef.current) return;
    loggedFilterSignatureRef.current = signature;
    void logAnalyticsEvent('template_filter_applied', {
      difficulty: selectedDifficulty,
      travelStyle: selectedStyles,
      hasDurationFilter: durationRange.min !== undefined || durationRange.max !== undefined,
      hasBudgetFilter: budgetRange.min !== undefined || budgetRange.max !== undefined,
      hasTopicFilter: selectedTopicTags.length > 0,
      featuredOnly,
    });
  }, [
    visible,
    selectedDifficulty,
    selectedStyles,
    durationRange,
    budgetRange,
    featuredOnly,
    selectedTopicTags,
  ]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedDifficulty.length > 0) count++;
    if (selectedStyles.length > 0) count++;
    if (durationRange.min !== undefined || durationRange.max !== undefined) count++;
    if (budgetRange.min !== undefined || budgetRange.max !== undefined) count++;
    if (featuredOnly) count++;
    if (selectedTopicTags.length > 0) count++;
    return count;
  }, [selectedDifficulty, selectedStyles, durationRange, budgetRange, featuredOnly, selectedTopicTags]);

  // Clear topic, difficulty, style, and search filters — keeps current quick view.
  const handleClearFilters = useCallback(() => {
    setSelectedDifficulty([]);
    setSelectedStyles([]);
    setDurationRange({});
    setBudgetRange({});
    setFeaturedOnly(false);
    clearTopicFilters();
    setSearchQuery('');
    setLocalSearchInput('');
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, [clearTopicFilters]);

  const handleQuickViewPress = useCallback(
    (option: TemplateQuickView) => {
      if (option === 'all') {
        handleClearFilters();
        setQuickView('all');
        return;
      }
      if (quickView === option) {
        setQuickView('all');
        return;
      }
      setQuickView(option);
    },
    [handleClearFilters, quickView],
  );

  const handleShowAllTemplates = useCallback(() => {
    handleQuickViewPress('all');
  }, [handleQuickViewPress]);

  // Handle search input with debounce to prevent keyboard dismissal
  const handleSearchInputChange = useCallback((text: string) => {
    setLocalSearchInput(text);
    
    // Clear existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Debounce the actual search query update
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(text);
    }, 300);
  }, []);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearchInput('');
    setSearchQuery('');
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Toggle filter selection
  const toggleDifficulty = (difficulty: TemplateDifficulty) => {
    setSelectedDifficulty((prev) =>
      prev.includes(difficulty) ? prev.filter((d) => d !== difficulty) : [...prev, difficulty]
    );
  };

  const toggleStyle = (style: TravelStyle) => {
    setSelectedStyles((prev) =>
      prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style]
    );
  };

  const toggleTopicTag = (tag: string) => {
    setSelectedTopicTags((prev) => {
      const next = prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag];
      if (next.length === 0) {
        onInitialTopicConsumed?.();
      }
      return next;
    });
  };

  const handleCollectionPress = useCallback((tag: string) => {
    setSelectedTopicTags([tag]);
    setQuickView('all');
    setShowFilters(false);
  }, []);

  // Render sticky header with close button - always visible at top
  const renderStickyHeader = () => (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: theme.spacing(2),
        paddingTop: insets.top + theme.spacing(1),
        paddingBottom: theme.spacing(1.5),
        backgroundColor: theme.colors.background,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderMuted,
      }}
    >
      <SubHeading>Trip Templates</SubHeading>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
        {onCreateTemplate && (
          <Pressable
            onPress={(e) => {
              console.log('[TemplateBrowser] Create Template button pressed');
              console.log('[TemplateBrowser] onCreateTemplate function:', typeof onCreateTemplate);
              e?.stopPropagation?.();
              onCreateTemplate();
            }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: theme.spacing(1.5),
              paddingVertical: theme.spacing(0.75),
              borderRadius: theme.radii.md,
              backgroundColor: theme.colors.primary,
              gap: theme.spacing(0.5),
            }}
            accessibilityRole="button"
            accessibilityLabel="Create a new trip template"
            accessibilityHint="Opens the template builder"
          >
            <Ionicons name="add" size={18} color={theme.colors.surface} />
            <Body style={{ color: theme.colors.surface, fontSize: 14, fontWeight: '600' }}>
              Create
            </Body>
          </Pressable>
        )}
        <Pressable
          onPress={onClose}
          style={{
            padding: theme.spacing(1),
            borderRadius: theme.radii.full,
            backgroundColor: theme.colors.surfaceAlt,
          }}
          accessibilityLabel="Close template browser"
          accessibilityRole="button"
          accessibilityHint="Closes template browser and returns to previous screen"
        >
          <Ionicons name="close" size={24} color={theme.colors.text} />
        </Pressable>
      </View>
    </View>
  );

  // Render scrollable header content (search, filters, etc.)
  const renderHeader = () => (
    <View>
      {/* Search Bar */}
      <View style={{ paddingHorizontal: theme.spacing(2), paddingTop: theme.spacing(1.5), paddingBottom: theme.spacing(1.5) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: theme.colors.background,
            borderRadius: theme.radii.md,
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
            paddingHorizontal: theme.spacing(1.5),
            paddingVertical: theme.spacing(1),
            gap: theme.spacing(1),
          }}
        >
          <Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} />
          <TextInput
            ref={searchInputRef}
            id="template-browser-search"
            name="template-browser-search"
            value={localSearchInput}
            onChangeText={handleSearchInputChange}
            placeholder="Search destinations, activities..."
            placeholderTextColor={theme.colors.textSecondary}
            style={{
              flex: 1,
              fontSize: 15,
              color: theme.colors.text,
              paddingVertical: 0,
            }}
            autoCorrect={false}
            returnKeyType="search"
            accessibilityLabel="Search templates"
            accessibilityHint="Type to filter templates by destination or activity"
          />
          {localSearchInput.length > 0 && (
            <Pressable
              onPress={handleClearSearch}
              accessibilityRole="button"
              accessibilityLabel="Clear template search"
              accessibilityHint="Clears current template search text"
            >
              <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
            </Pressable>
          )}
        </View>
      </View>

      {/* Filter and Sort Bar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(1),
          paddingHorizontal: theme.spacing(2),
          paddingBottom: theme.spacing(1.5),
        }}
      >
        {/* Filter Button */}
        <Pressable
          onPress={() => setShowFilters(!showFilters)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            paddingHorizontal: theme.spacing(1.5),
            paddingVertical: theme.spacing(0.75),
            borderRadius: theme.radii.md,
            borderWidth: 1,
            borderColor: activeFilterCount > 0 ? theme.colors.primary : theme.colors.borderMuted,
            backgroundColor: activeFilterCount > 0 ? `${theme.colors.primary}15` : 'transparent',
          }}
          accessibilityRole="button"
          accessibilityLabel={showFilters ? 'Hide template filters' : 'Show template filters'}
          accessibilityHint="Opens filter options for template results"
          accessibilityState={{ expanded: showFilters }}
        >
          <Ionicons
            name="filter-outline"
            size={16}
            color={activeFilterCount > 0 ? theme.colors.primary : theme.colors.text}
          />
          <Caption
            color={activeFilterCount > 0 ? theme.colors.primary : theme.colors.text}
            style={{ fontWeight: '600' }}
          >
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Caption>
        </Pressable>

        {/* Quick view: All, Popular, Budget, Duration — one active at a time */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            {QUICK_VIEW_OPTIONS.map((option) => {
              const isSelected = quickView === option;
              const label = QUICK_VIEW_LABELS[option];

              return (
                <Pressable
                  key={option}
                  onPress={() => handleQuickViewPress(option)}
                  style={{
                    paddingHorizontal: theme.spacing(1.5),
                    paddingVertical: theme.spacing(0.75),
                    borderRadius: theme.radii.md,
                    borderWidth: 1,
                    borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                    backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={option === 'all' ? 'All templates' : `${label} sort`}
                  accessibilityHint={
                    option === 'all'
                      ? 'Clears filters and shows every template'
                      : isSelected
                        ? 'Deselects this sort and returns to all templates'
                        : `Sort templates by ${label.toLowerCase()}`
                  }
                  accessibilityState={{ selected: isSelected }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    {isSelected && (
                      <Ionicons name="checkmark-circle" size={12} color={theme.colors.primary} />
                    )}
                    <Caption
                      color={isSelected ? theme.colors.primary : theme.colors.text}
                      style={{ fontWeight: isSelected ? '600' : '400' }}
                    >
                      {label}
                    </Caption>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </ScrollView>
      </View>

      {/* Featured Collections */}
      {TEMPLATE_TOPIC_FILTERS.length > 0 && (
        <View style={{ paddingBottom: theme.spacing(1.5) }}>
          <View style={{ paddingHorizontal: theme.spacing(2), marginBottom: theme.spacing(1) }}>
            <Caption style={{ fontWeight: '700' }}>Featured collections</Caption>
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={{ flexDirection: 'row', gap: theme.spacing(1), paddingHorizontal: theme.spacing(2) }}>
              {TEMPLATE_TOPIC_FILTERS.map((collection) => {
                const isSelected = selectedTopicTags.includes(collection.tag);
                return (
                  <Pressable
                    key={collection.tag}
                    onPress={() => handleCollectionPress(collection.tag)}
                    style={{
                      width: Math.min(280, Math.max(230, width * 0.68)),
                      padding: theme.spacing(1.5),
                      borderRadius: theme.radii.lg,
                      borderWidth: 1,
                      borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                      backgroundColor: isSelected ? `${theme.colors.primary}15` : theme.colors.surface,
                      gap: theme.spacing(0.75),
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={collection.ctaLabel}
                    accessibilityState={{ selected: isSelected }}
                  >
                    <Caption color={theme.colors.primary} style={{ fontWeight: '700' }}>
                      {collection.eyebrow}
                    </Caption>
                    <Body style={{ fontWeight: '700' }}>{collection.label}</Body>
                    <Caption color={theme.colors.textSecondary} numberOfLines={2}>
                      {collection.description}
                    </Caption>
                  </Pressable>
                );
              })}
            </View>
          </ScrollView>
        </View>
      )}

      {/* Filter Panel */}
      {showFilters && (
        <Card variant="outlined" style={{ marginHorizontal: theme.spacing(2), marginBottom: theme.spacing(1.5) }}>
          <View style={{ gap: theme.spacing(2) }}>
            {/* Difficulty Filter */}
            <View>
              <Caption style={{ marginBottom: theme.spacing(1), fontWeight: '600' }}>Difficulty</Caption>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                {(['easy', 'moderate', 'adventurous'] as TemplateDifficulty[]).map((diff) => {
                  const isSelected = selectedDifficulty.includes(diff);
                  return (
                    <Pressable
                      key={diff}
                      onPress={() => toggleDifficulty(diff)}
                      style={{
                        paddingHorizontal: theme.spacing(1.5),
                        paddingVertical: theme.spacing(0.75),
                        borderRadius: theme.radii.md,
                        borderWidth: 1,
                        borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                        backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={`${diff} difficulty`}
                      accessibilityHint={isSelected ? 'Filter selected' : 'Toggle difficulty filter'}
                      accessibilityState={{ selected: isSelected }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                        {isSelected && (
                          <Ionicons name="checkmark-circle" size={12} color={theme.colors.primary} />
                        )}
                        <Caption
                          color={isSelected ? theme.colors.primary : theme.colors.text}
                          style={{ textTransform: 'capitalize' }}
                        >
                          {diff}
                        </Caption>
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            {/* Travel Style Filter */}
            <View>
              <Caption style={{ marginBottom: theme.spacing(1), fontWeight: '600' }}>Travel Style</Caption>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                {(['cultural', 'adventure', 'relaxation', 'food', 'urban', 'nature', 'beach'] as TravelStyle[]).map(
                  (style) => {
                    const isSelected = selectedStyles.includes(style);
                    return (
                      <Pressable
                        key={style}
                        onPress={() => toggleStyle(style)}
                        style={{
                          paddingHorizontal: theme.spacing(1.5),
                          paddingVertical: theme.spacing(0.75),
                          borderRadius: theme.radii.md,
                          borderWidth: 1,
                          borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                          backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
                        }}
                        accessibilityRole="button"
                        accessibilityLabel={`${style} travel style`}
                        accessibilityHint={isSelected ? 'Filter selected' : 'Toggle travel style filter'}
                        accessibilityState={{ selected: isSelected }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                          {isSelected && (
                            <Ionicons name="checkmark-circle" size={12} color={theme.colors.primary} />
                          )}
                          <Caption
                            color={isSelected ? theme.colors.primary : theme.colors.text}
                            style={{ textTransform: 'capitalize' }}
                          >
                            {style}
                          </Caption>
                        </View>
                      </Pressable>
                    );
                  }
                )}
              </View>
            </View>

            {/* Topic / collection */}
            <View>
              <Caption style={{ marginBottom: theme.spacing(1), fontWeight: '600' }}>Topics</Caption>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                <Pressable
                  onPress={clearTopicFilters}
                  style={{
                    paddingHorizontal: theme.spacing(1.5),
                    paddingVertical: theme.spacing(0.75),
                    borderRadius: theme.radii.md,
                    borderWidth: 1,
                    borderColor:
                      selectedTopicTags.length === 0 ? theme.colors.primary : theme.colors.borderMuted,
                    backgroundColor:
                      selectedTopicTags.length === 0 ? `${theme.colors.primary}15` : 'transparent',
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="All topics"
                  accessibilityHint="Clears topic filters to show templates from every collection"
                  accessibilityState={{ selected: selectedTopicTags.length === 0 }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    {selectedTopicTags.length === 0 && (
                      <Ionicons name="checkmark-circle" size={12} color={theme.colors.primary} />
                    )}
                    <Caption
                      color={selectedTopicTags.length === 0 ? theme.colors.primary : theme.colors.text}
                      style={{ fontWeight: '600' }}
                    >
                      All
                    </Caption>
                  </View>
                </Pressable>
                {TEMPLATE_TOPIC_FILTERS.map(({ tag, label }) => {
                  const isSelected = selectedTopicTags.includes(tag);
                  return (
                    <Pressable
                      key={tag}
                      onPress={() => toggleTopicTag(tag)}
                      style={{
                        paddingHorizontal: theme.spacing(1.5),
                        paddingVertical: theme.spacing(0.75),
                        borderRadius: theme.radii.md,
                        borderWidth: 1,
                        borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                        backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={`${label} templates`}
                      accessibilityHint={
                        isSelected ? `Removes ${label} filter` : `Shows only ${label} trip templates`
                      }
                      accessibilityState={{ selected: isSelected }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                        {isSelected && (
                          <Ionicons name="checkmark-circle" size={12} color={theme.colors.primary} />
                        )}
                        <Caption color={isSelected ? theme.colors.primary : theme.colors.text} style={{ fontWeight: '600' }}>
                          {label}
                        </Caption>
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            {/* Clear Filters Button */}
            {activeFilterCount > 0 && (
              <Button variant="text" label="Show all templates" onPress={handleShowAllTemplates} size="small" />
            )}
          </View>
        </Card>
      )}

      {/* Results Count */}
      {!loading && (
        <View style={{ paddingHorizontal: theme.spacing(2), paddingBottom: theme.spacing(1.5) }}>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
            {filteredAndSortedTemplates.length === marketplaceTemplates.length
              ? `${marketplaceTemplates.length} ${marketplaceTemplates.length === 1 ? 'template' : 'templates'}`
              : `${filteredAndSortedTemplates.length} of ${marketplaceTemplates.length} templates`}
            {localSearchInput && ` matching "${localSearchInput}"`}
            {selectedTopicTags.length > 0 &&
              ` · Topic: ${selectedTopicTags.map((tag) => TEMPLATE_TOPIC_FILTERS.find((f) => f.tag === tag)?.label ?? tag).join(', ')}`}
          </Caption>
        </View>
      )}

      {templateLoadError && templates.length > 0 && (
        <View style={{ paddingHorizontal: theme.spacing(2), paddingBottom: theme.spacing(1.5) }}>
          <ErrorState
            title="Couldn't refresh templates"
            message={templateLoadError}
            onRetry={() => loadTemplates(false)}
            retryLabel="Retry"
          />
        </View>
      )}
    </View>
  );

  // Render empty state
  const renderEmptyState = () => (
    <View style={{ padding: theme.spacing(4), alignItems: 'center' }}>
      <Ionicons name="map-outline" size={64} color={theme.colors.textSecondary} />
      <SubHeading style={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(1) }}>
        No Templates Found
      </SubHeading>
      <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
        {searchQuery || activeFilterCount > 0
          ? 'Try adjusting your search or filters'
          : 'Check back soon for curated trip templates'}
      </Body>
      {(searchQuery || activeFilterCount > 0) && (
        <Button
          variant="outlined"
          label="Show all templates"
          onPress={handleShowAllTemplates}
          size="small"
          style={{ marginTop: theme.spacing(2) }}
        />
      )}
    </View>
  );

  // Render loading state
  const renderLoadingState = () => (
    <View style={{ padding: theme.spacing(2), gap: theme.spacing(2) }}>
      <TemplateCardSkeleton />
      <TemplateCardSkeleton />
      <TemplateCardSkeleton />
    </View>
  );

  const renderLoadErrorState = () => (
    <View style={{ padding: theme.spacing(2) }}>
      <ErrorState
        title="Couldn't load templates"
        message={templateLoadError || "Couldn't load templates. Check your connection and try again."}
        onRetry={() => loadTemplates(false)}
        retryLabel="Retry"
      />
    </View>
  );

  const renderTemplateItem = useCallback(
    ({ item }: { item: TripTemplate }) => (
      <View style={{ paddingHorizontal: theme.spacing(2), marginBottom: theme.spacing(2) }}>
        <TemplateCard
          template={item}
          onPress={(selected) => {
            void logAnalyticsEvent('template_viewed', {
              templateId: selected.id,
              templateTitle: selected.title,
              destination: selected.destination,
              duration: selected.duration,
              source: searchQuery.trim() ? 'search' : initialTopicTag ? 'deeplink' : 'browser',
              topicTag: selectedTopicTags[0] ?? initialTopicTag,
            });
            onSelectTemplate(selected, 'browser', selectedTopicTags[0] ?? initialTopicTag);
          }}
          showSaveAction
          userId={firebaseUser?.uid || ''}
        />
      </View>
    ),
    [firebaseUser?.uid, initialTopicTag, onSelectTemplate, searchQuery, selectedTopicTags, theme],
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      onDismiss={onDismiss}
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        {/* Sticky Header - Always visible at top */}
        {renderStickyHeader()}
        
        {loading && templates.length === 0 ? (
          <>
            {renderHeader()}
            {renderLoadingState()}
          </>
        ) : templateLoadError && templates.length === 0 ? (
          <>
            {renderHeader()}
            {renderLoadErrorState()}
          </>
        ) : (
          <FlatList
            data={filteredAndSortedTemplates}
            renderItem={renderTemplateItem}
            keyExtractor={(item) => item.id}
            // IMPORTANT: Passing a function here can cause the header to be re-mounted on each render
            // (function identity changes), which makes the search TextInput lose focus and dismiss the keyboard.
            // Passing a React element avoids unmount/remount and keeps the keyboard stable while typing.
            ListHeaderComponent={renderHeader()}
            ListEmptyComponent={renderEmptyState}
            contentContainerStyle={{
              paddingBottom: theme.spacing(2) + insets.bottom,
            }}
            showsVerticalScrollIndicator={false}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
            keyboardShouldPersistTaps="always"
            keyboardDismissMode="none"
          />
        )}
      </View>
    </Modal>
  );
}

